import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan, useStandardConfirms } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { roleAdminService } from 'api/services';
import type { PermissionCatalogDto, RoleSummaryDto } from 'api/types';
import RoleModal, { roleKindBadge, type RoleModalMode } from 'components/modals/modules/settings/RoleModal';

// ============================================================================
// ROLES & PERMISSIONS (Settings) — an authorised user creates roles and ticks,
// screen by screen, what each role may do. The seeds only give a fresh install
// its default roles; after that the rights are the customer's
// (fairbooks-db/CLAUDE.md "Role permissions").
//   * FBP Super Administrator — the locked system role: read-only here.
//   * Product roles (FBP_*): rights and name editable, not deletable.
//   * Custom roles: the customer's own; deletable while nobody holds them.
// The list is the house list screen (search, list / card view, filters, column
// selector, ⋮ row actions); a role opens in a tabbed modal (Details ·
// Permissions · Members) that minimizes to the bar. Changes reach signed-in
// users within a few seconds (permission cache).
// ============================================================================

type ViewMode = 'list' | 'grid';
type KindFilter = '' | 'LOCKED' | 'PRODUCT' | 'CUSTOM';
type UsersFilter = '' | 'WITH' | 'WITHOUT';

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'code', label: 'Code' },
  { id: 'name', label: 'Name' },
  { id: 'kind', label: 'Kind' },
  { id: 'userCount', label: 'Users' },
  { id: 'grantCount', label: 'Rights' },
  { id: 'description', label: 'Description' },
  { id: 'updatedAt', label: 'Last changed' },
  { id: 'actions', label: 'Actions', required: true },
];

const kindOf = (r: RoleSummaryDto): KindFilter => (!r.isMutable ? 'LOCKED' : r.isProductRole ? 'PRODUCT' : 'CUSTOM');

interface RolePermissionsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const RolePermissionsScreen: React.FC<RolePermissionsScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const can = useCan('role-permissions');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [roles, setRoles] = useState<RoleSummaryDto[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalogDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } = useColumnVisibility('role-permissions-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [kindDraft, setKindDraft] = useState<KindFilter>('');
  const [usersDraft, setUsersDraft] = useState<UsersFilter>('');
  const [kind, setKind] = useState<KindFilter>('');
  const [usersFilter, setUsersFilter] = useState<UsersFilter>('');

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<RoleModalMode>('view');
  const [modalRoleId, setModalRoleId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<'details' | 'permissions' | 'members'>('details');
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const minimizeRef = useRef<(() => void) | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRoles(await roleAdminService.getRoles()); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message || 'Could not load the roles.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    roleAdminService.getCatalog().then(setCatalog)
      .catch(err => notificationService.toast.error(err?.response?.data?.message || 'Could not load the screens.'));
  }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roles.filter(r => (!kind || kindOf(r) === kind)
      && (!usersFilter || (usersFilter === 'WITH' ? r.userCount > 0 : r.userCount === 0))
      && (!q || [r.code, r.name, r.description].some(v => v?.toLowerCase().includes(q))));
  }, [roles, search, kind, usersFilter]);

  // ---- opening the modal (restoring a minimized one where there is one) ----------
  const restoreIfMinimized = (modalId: string) => {
    if (!tabId || !isMinimized(modalId)) return false;
    const m = getMinimizedModal(modalId);
    dismissMinimized(modalId); m?.onRestore?.();
    return true;
  };

  const open = useCallback((mode: RoleModalMode, roleId: string | null, tab: 'details' | 'permissions' | 'members' = 'details') => {
    if (mode === 'view' || mode === 'edit') {
      if (restoreIfMinimized(`${tabId}-role-modal-edit-${roleId}`) || restoreIfMinimized(`${tabId}-role-modal-view-${roleId}`)) return;
    }
    if (modalOpen) minimizeRef.current?.();
    if (mode === 'create' || mode === 'copy') {
      let next = 1;
      if (tabId) while (isMinimized(`${tabId}-role-modal-${mode}-${next}`)) next++;
      setCreateInstanceId(String(next));
    }
    setModalMode(mode); setModalRoleId(roleId); setModalTab(tab); setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, modalOpen, isMinimized]);

  const handleRestore = useCallback((mode: RoleModalMode, roleId: string | null, instanceId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    setModalMode(mode); setModalRoleId(roleId); setModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (r: RoleSummaryDto) => {
    if (!(await confirmDelete({ resourceLabel: `the role ${r.name}`, message: `Delete the role ${r.name}? Its rights go with it. This cannot be undone.` }))) return;
    try {
      await roleAdminService.deleteRole(r.id);
      notificationService.toast.success(`Role ${r.name} deleted.`);
      load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete the role.');
    }
  }, [confirmDelete, load]);

  const rowActions = useCallback((r: RoleSummaryDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => open('view', r.id) }];
    if (can('UPDATE') && r.isMutable) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => open('edit', r.id) });
      items.push({ id: 'rights', label: 'Edit permissions', icon: <IconSystem.lock size={18} />, onClick: () => open('edit', r.id, 'permissions') });
    }
    items.push({ id: 'members', label: 'Members', icon: <IconSystem.users size={18} />, onClick: () => open('view', r.id, 'members') });
    if (can('CREATE')) items.push({ id: 'copy', label: 'Copy as new role', icon: <IconSystem.copy size={18} />, onClick: () => open('copy', r.id) });
    if (can('DELETE') && r.isMutable && !r.isProductRole) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, variant: 'danger', disabled: r.userCount > 0, onClick: () => handleDelete(r) });
    }
    return items;
  }, [can, open, handleDelete]);

  const columns: SyutGridColumn<RoleSummaryDto>[] = useMemo(() => [
    { id: 'code', header: 'Code', accessorKey: 'code', size: 190 },
    { id: 'name', header: 'Name', accessorKey: 'name', size: 230,
      cell: ({ row }) => (
        <button type="button" onClick={() => open('view', row.original.id)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', textAlign: 'left' }}>
          {row.original.name}
        </button>
      ) },
    { id: 'kind', header: 'Kind', size: 110, cell: ({ row }) => roleKindBadge(row.original) },
    { id: 'userCount', header: 'Users', accessorKey: 'userCount', size: 90, align: 'right' },
    { id: 'grantCount', header: 'Rights', accessorKey: 'grantCount', size: 90, align: 'right' },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 320, cell: ({ row }) => row.original.description || '—' },
    { id: 'updatedAt', header: 'Last changed', size: 170,
      cell: ({ row }) => (row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—') },
  ], [open]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Card View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(() => open('create', null), 'New Role'), visible: can('CREATE') },
    actionButtonFactory.refresh(load),
  ];

  const appliedCount = (kind ? 1 : 0) + (usersFilter ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="Roles & Permissions"
      description="Create roles and decide, screen by screen, what each role may do"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.lock size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: search, onChange: setSearch, placeholder: 'Search roles...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: m => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => { setKindDraft(kind); setUsersDraft(usersFilter); setFilterOpen(!filterOpen); }, isActive: filterOpen, appliedCount }}
      customToolbarItems={<ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns} onApply={applyColumns} onReset={resetColumns} />}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Kind</label>
              <select className="form-input" value={kindDraft} onChange={e => setKindDraft(e.target.value as KindFilter)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}>
                <option value="">All</option><option value="LOCKED">Locked</option><option value="PRODUCT">Product</option><option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Users</label>
              <select className="form-input" value={usersDraft} onChange={e => setUsersDraft(e.target.value as UsersFilter)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}>
                <option value="">All</option><option value="WITH">Held by someone</option><option value="WITHOUT">Held by nobody</option>
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setKindDraft(''); setUsersDraft(''); setKind(''); setUsersFilter(''); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => { setKind(kindDraft); setUsersFilter(usersDraft); setFilterOpen(false); }}>Apply</button>
          </div>
        </div>
      )}
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={shown} columns={columns} visibleColumns={['actions', ...visibleColumns.filter(c => c !== 'actions')]} enablePagination defaultPageSize={25}
            rowActions={rowActions} emptyMessage={loading ? 'Loading...' : 'No roles match.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
              {shown.map(r => (
                <div key={r.id} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.code}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0', cursor: 'pointer' }} onClick={() => open('view', r.id)}>{r.name}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div>{roleKindBadge(r)}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {r.userCount} user{r.userCount === 1 ? '' : 's'} · {r.grantCount} rights
                  </div>
                  {r.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{r.description}</div>}
                </div>
              ))}
              {shown.length === 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{loading ? 'Loading...' : 'No roles match.'}</div>}
            </div>
          </div>
        )}
      </div>

      <RoleModal isOpen={modalOpen} mode={modalMode} roleId={modalRoleId} roles={roles} catalog={catalog}
        canEdit={can('UPDATE') || (can('CREATE') && (modalMode === 'create' || modalMode === 'copy'))}
        initialTab={modalTab}
        onEdit={id => { setModalMode('edit'); setModalRoleId(id); }}
        onSaved={load}
        onClose={() => setModalOpen(false)}
        tabId={tabId} moduleName={moduleName || 'Roles & Permissions'}
        createInstanceId={createInstanceId} minimizeRef={minimizeRef} onRestoreModal={handleRestore} />
    </EnterpriseScreenShell>
  );
};

export default RolePermissionsScreen;
